// @flow

import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import Group from 'sail/Group';
import Box from 'sail/Box';
import {AlignBox} from 'sail/Align';
import {Button} from 'sail/Button';
import {Body, BodyAlt, BodyAltTextProps, Caption} from 'sail/Text';
import {InputBase, TextInput} from 'sail/Input';
import Icon from 'sail/Icon';
import type {IconType} from 'sail/Icon';
import {InlineLink} from 'sail/InlineLink';
import {useApi, useAccount} from 'docs/data';
import {defineMessages, FormattedMessage} from 'react-intl';
import type {Article} from 'docs/data';

import {Csat} from './Csat';
import {CountrySelector} from '../sidebar/footer/CountrySelector';
import {LocaleSelector} from '../sidebar/footer/LocaleSelector';

import './Footer.scss';

type FooterLinkProps = {|
  label: FormattedMessage,
  icon: IconType,
|};

const messages = defineMessages({
  signUp: {
    id: 'docsShell.footer.signUp',
    description: 'Placeholder text for input box',
    defaultMessage: 'Sign up',
  },
  youCanUnsubscribe: {
    id: 'docsShell.footer.youCanUnsubscribe',
    description: 'Note under the newsletter sign up box',
    defaultMessage:
      'You can unsubscribe at any time. Read our <a>privacy policy</a>.',
  },
  devDigestThankYou: {
    id: 'docsShell.footer.devDigestThankYou',
    description:
      'Note thanking someone for signing up for dev digest newsletter',
    defaultMessage:
      'Thank you, we’ll let you know when our next dev digest goes out.',
  },
  devDigestFailedEmail: {
    id: 'docsShell.footer.devDigestFailedEmail',
    description:
      'Note that signing up for dev digest newsletter failed due to invalid email',
    defaultMessage:
      'Sorry, your signup was unsuccessful due to an invalid email',
  },
  devDigestFailed: {
    id: 'docsShell.footer.devDigestFailed',
    description: 'Note that signing up for dev digest newsletter failed',
    defaultMessage: 'Sorry, your signup was unsuccessful',
  },
  questions: {
    id: 'docsShell.footer.questions',
    description: 'Note in the footer',
    defaultMessage: 'Questions? <a>Contact us.</a>',
  },
  devTutorials: {
    id: 'docsShell.footer.devTutorials',
    description: 'Note in the footer',
    defaultMessage: 'View developer tutorials on <a>YouTube.</a>',
  },
  changeLog: {
    id: 'docsShell.footer.changeLog',
    description: 'Note in the footer',
    defaultMessage: 'Check out our <a>product changelog</a>.',
  },
  signUpForUpdates: {
    id: 'docsShell.footer.signUpForUpdates',
    description: 'Note in the footer',
    defaultMessage: 'Sign up for developer updates:',
  },
  poweredByMarkdoc: {
    id: 'docsShell.footer.poweredByMarkdoc.md',
    description: 'Note in the footer',
    defaultMessage: 'Powered by <a>Markdoc</a>',
  },
});

const FooterLink = (props: FooterLinkProps) => {
  return (
    <AlignBox reference={BodyAltTextProps} margin={{top: 4}}>
      <Box margin={{right: 12}}>
        <Icon icon={props.icon} />
      </Box>
      <BodyAlt>{props.label}</BodyAlt>
    </AlignBox>
  );
};

type DevDigestSignupProps = {|
  email: string,
|};

const DevDigestSignup = (props: DevDigestSignupProps) => {
  const [email, setEmail] = useState(props.email);
  const [subscribeResult, setSubscribeResult] = useState('');
  const [pending, setPending] = useState(false);
  const {api} = useApi();

  const handleSubmit = async () => {
    const params = {
      email,
      list: 'developerdigest',
    };
    setPending(true);
    const response = await api.emailSignup(params);
    if (response.emailSignupSuccessful) {
      setSubscribeResult(<FormattedMessage {...messages.devDigestThankYou} />);
    } else if (response.error === 'invalid_email') {
      setSubscribeResult(
        <FormattedMessage {...messages.devDigestFailedEmail} />,
      );
    } else {
      setSubscribeResult(<FormattedMessage {...messages.devDigestFailed} />);
    }
    setPending(false);
  };

  return (
    <Box
      margin={{top: 4}}
      id="footer-digest-signup"
      className="Footer-digest-signup"
    >
      <AnimatePresence initial={false} exitBeforeEnter>
        <motion.div
          key={subscribeResult ? 'result' : 'prompt'}
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
        >
          {subscribeResult ? (
            <Box style={{width: '100%'}} flex={{justifyContent: 'flex-end'}}>
              <Body>{subscribeResult}</Body>
            </Box>
          ) : (
            <Group spacing={{vertical: 8}}>
              <label htmlFor="Footer-DevDigest">
                <BodyAlt>
                  <FormattedMessage {...messages.signUpForUpdates} />
                </BodyAlt>
              </label>
              <Box flex={{direction: 'column'}}>
                <InputBase size="large" width="maximized">
                  <TextInput
                    type="email"
                    id="Footer-DevDigest"
                    width="maximized"
                    onChange={setEmail}
                    value={email}
                  />
                  <Button
                    color="white"
                    label={<FormattedMessage {...messages.signUp} />}
                    size="large"
                    ghost
                    onClick={handleSubmit}
                    pending={pending}
                    disabled={!email}
                  />
                </InputBase>
                <Box margin={{top: 8}}>
                  <Caption>
                    <FormattedMessage
                      {...messages.youCanUnsubscribe}
                      values={{
                        a: (msg) => (
                          <InlineLink href="/privacy">{msg}</InlineLink>
                        ),
                      }}
                    />
                  </Caption>
                </Box>
              </Box>
            </Group>
          )}
        </motion.div>
      </AnimatePresence>
    </Box>
  );
};

type FooterProps = {|
  article: Article,
|};

export const Footer = (props: FooterProps) => {
  const {article} = props;
  const {account} = useAccount();

  return (
    <Group spacing={20} margin={{top: 80}} className="Footer">
      {article && article.features.csat && <Csat article={article} />}
      <Box
        padding={{vertical: 32}}
        flex={{justifyContent: 'space-between'}}
        divider={{color: 'light', width: 1, side: 'top'}}
        className="Footer-content"
      >
        <Group direction="vertical" spacing={8} className="Footer-links">
          <FooterLink
            label={
              <FormattedMessage
                {...messages.questions}
                values={{
                  a: (msg) => (
                    <InlineLink
                      href="https://support.stripe.com/"
                      target="_blank"
                    >
                      {msg}
                    </InlineLink>
                  ),
                }}
              />
            }
            icon="support"
          />
          <FooterLink
            label={
              <FormattedMessage
                {...messages.devTutorials}
                values={{
                  a: (msg) => (
                    <InlineLink
                      href="https://www.youtube.com/stripedevelopers/"
                      target="_blank"
                    >
                      {msg}
                    </InlineLink>
                  ),
                }}
              />
            }
            icon="video"
          />
          <FooterLink
            label={
              <FormattedMessage
                {...messages.changeLog}
                values={{
                  a: (msg) => (
                    <InlineLink
                      href="https://stripe.com/blog/changelog"
                      target="_blank"
                    >
                      {msg}
                    </InlineLink>
                  ),
                }}
              />
            }
            icon="doc"
          />
          <Box className="Footer-powered-by">
            <BodyAlt>
              <FormattedMessage
                {...messages.poweredByMarkdoc}
                values={{
                  a: (label) => (
                    <InlineLink href="https://markdoc.io" target="_blank">
                      {label}
                    </InlineLink>
                  ),
                }}
              />
            </BodyAlt>
          </Box>
          <Group
            className="Footer-selectors"
            direction="horizontal"
            spacing={8}
          >
            <CountrySelector showLabel={false} />
            <LocaleSelector showLabel={false} />
          </Group>
        </Group>
        <DevDigestSignup
          email={account.user && account.user.email ? account.user.email : ''}
        />
      </Box>
    </Group>
  );
};
